import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { getErrorMessage } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import PasswordChangeFields, { PASSWORD_CHANGE_INITIAL, PasswordChangeSchema, passwordFieldErrors } from '../form/PasswordChangeFields';
import BusyButton from '../form/BusyButton';

/** Lets the signed-in user replace their password from the account menu. */
const ChangePasswordDialog = ({ open, onClose }) => {
  const toast = useToast();
  const { changePassword } = useAuth();
  const [error, setError] = useState(null);
  // The dialog stays open until the request has finished.
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (values, { setErrors, setSubmitting }) => {
    setError(null);
    setSaving(true);
    try {
      await changePassword(values.current_password, values.new_password);
      toast.success('Password changed');
      onClose();
    } catch (err) {
      const fieldErrors = passwordFieldErrors(err);
      if (fieldErrors) setErrors(fieldErrors);
      else setError(getErrorMessage(err, 'Could not change your password.'));
      setSubmitting(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="change-password-title" TransitionProps={{ onExited: () => setError(null) }}>
      {open && (
        <Formik initialValues={PASSWORD_CHANGE_INITIAL} validationSchema={PasswordChangeSchema} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form noValidate>
              <DialogTitle id="change-password-title">Change password</DialogTitle>
              <DialogContent>
                <PasswordChangeFields error={error} />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={isSubmitting}>Cancel</Button>
                <BusyButton type="submit" variant="contained" busy={isSubmitting}>
                  Change password
                </BusyButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default ChangePasswordDialog;
